import {
  EnrollmentMeetingPurchaseQueryQuery,
  ParentChildrenQuery
} from "@outschool/gql-frontend-generated";
import { gql } from "@outschool/ui-apollo";
import _ from "lodash";

import * as Enrollment from "../../../../../shared/Enrollment";
import {
  ActivityEnrollQueryQuery,
  GetLearnerItems_EnrollmentFragment,
  GetLearnerItems_EnrollmentWithMeetingPurchasesFragment
} from "../../../../../shared/generated/graphql";
import EnrollmentWarnings from "./EnrollmentWarnings";
import { LearnerItemType, getLearnerUidsNearAgeRange } from "./utils";

const randString = (): string =>
  Math.random().toString(36).substring(2, 15) +
  Math.random().toString(36).substring(2, 15);

export function defaultLearnerItem(): LearnerItemType {
  return {
    key: randString(),
    checked: true,
    learner: {
      // @ts-ignore TS(2322): Type 'null' is not assignable to type 'string'. -- Removing this breaks creating a new learner in checkout... TODO investigate solution & add test coverage
      uid: null,
      name: "",
      age: null,
      date_of_birth: null
    }
  };
}

type GetLearnerItemsArgs = {
  activity:
    | NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"]
    | NonNullable<
        EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]
      >["activity"];
  enrollingParent: ActivityEnrollQueryQuery["currentUser"];
  onlyAllowSingleLearner: boolean;
  ignoreStrictAgeRange: boolean;
  lastSubmittedEnrollments?: GetLearnerItems_EnrollmentFragment[];
  enrollingParentEnrollments?: GetLearnerItems_EnrollmentFragment[];
  enrollmentsInOtherSections?: GetLearnerItems_EnrollmentFragment[];
  conflictingEnrollments?: GetLearnerItems_EnrollmentFragment[];
  pendingEnrollments?:
    | GetLearnerItems_EnrollmentFragment[]
    | GetLearnerItems_EnrollmentWithMeetingPurchasesFragment[];
};

export function getLearnerItems({
  activity,
  enrollingParent,
  onlyAllowSingleLearner,
  ignoreStrictAgeRange,
  lastSubmittedEnrollments = [],
  enrollingParentEnrollments = [],
  enrollmentsInOtherSections = [],
  conflictingEnrollments = [],
  pendingEnrollments = []
}: GetLearnerItemsArgs): LearnerItemType[] {
  const pendingEnrollmentLearners = pendingEnrollments.map(e => ({
    learner: e.learner,
    hasOverlappingPendingEnrollmentMeetingPurchase:
      // @ts-ignore
      e.hasOverlappingPendingEnrollmentMeetingPurchase
  }));
  const alreadyEnrolledLearners = _.uniqBy(
    enrollingParentEnrollments
      .filter(e => Enrollment.isValid(e) && !e.isEnded && !e.isPaused)
      .map(e => e.learner),
    "uid"
  );
  const recentLearners = _.uniqBy(
    _.compact(lastSubmittedEnrollments.map(e => e.learner)),
    "uid"
  )
    .filter(rl => !alreadyEnrolledLearners.some(ael => ael?.uid === rl.uid))
    .filter(
      rl =>
        !pendingEnrollmentLearners.some(
          // @ts-ignore
          ({ learner: pel }) => pel.uid === rl.uid
        )
    );
  const learners = enrollingParent
    ? enrollingParent.children
        .filter(l => !alreadyEnrolledLearners.some(ael => ael?.uid === l.uid))
        .filter(
          l =>
            !pendingEnrollmentLearners.some(
              // @ts-ignore
              ({ learner: pel }) => pel.uid === l.uid
            )
        )
        .filter(l => !recentLearners.some(rl => rl.uid === l.uid))
    : [];

  const noLearners = learners.length === 0 && recentLearners.length === 0;
  const newItems = noLearners ? [defaultLearnerItem()] : [];

  const recentLearnerItems = recentLearners.map(learner => ({
    key: learner.uid,
    checked: false,
    hasEmptyName: !learner.name || learner.name.trim() === "",
    conflictingEnrollment: conflictingEnrollments.find(
      enrollment => enrollment?.learner?.uid === learner.uid
    ),
    otherSectionEnrollment: enrollmentsInOtherSections.find(
      enrollment => enrollment?.learner?.uid === learner.uid
    ),
    learner
  }));

  const otherLearnerItems = learners
    .filter(l => !recentLearners.some(rl => rl.uid === l.uid))
    .map(learner => ({
      key: learner.uid,
      checked: false,
      hasEmptyName: !learner.name || learner.name.trim() === "",
      conflictingEnrollment: conflictingEnrollments.find(
        enrollment => enrollment?.learner?.uid === learner.uid
      ),
      otherSectionEnrollment: enrollmentsInOtherSections.find(
        enrollment => enrollment?.learner?.uid === learner.uid
      ),
      learner
    }));

  const strictAgeRange = !!(activity.strictAgeRange && !ignoreStrictAgeRange);

  // selection logic
  const maxAge = activity.age_max ?? 200;
  const minAge = activity.age_min ?? 0;

  const hasCheckedLearners = chooseCheckedLearners(
    recentLearnerItems.filter(
      li => !(activity.hasTeacherSchedule && !!li.otherSectionEnrollment)
    ),
    otherLearnerItems.filter(
      li => !(activity.hasTeacherSchedule && !!li.otherSectionEnrollment)
    ),
    minAge,
    maxAge,
    strictAgeRange,
    onlyAllowSingleLearner
  );
  if (!hasCheckedLearners) {
    chooseCheckedLearners(
      recentLearnerItems.filter(
        li => !(activity.hasTeacherSchedule && !!li.otherSectionEnrollment)
      ),
      otherLearnerItems.filter(
        li => !(activity.hasTeacherSchedule && !!li.otherSectionEnrollment)
      ),
      minAge - (activity?.strictAgeRange?.min ?? 0),
      maxAge + (activity?.strictAgeRange?.max ?? 0),
      strictAgeRange,
      onlyAllowSingleLearner
    );
  }

  const alreadyEnrolledItems = alreadyEnrolledLearners.map(learner => {
    const isPrepay = _.orderBy(
      (
        enrollingParentEnrollments as NonNullable<
          ParentChildrenQuery["parentByEmail"]
        >["enrollments"]
      )?.filter(enrollment => enrollment?.learner?.uid === learner?.uid),
      enrollment => enrollment?.ongoingEndTime,
      "desc"
    )[0]?.isPrepay;

    return {
      key: learner!.uid,
      checked: !isPrepay,
      alreadyEnrolled: true,
      learner: learner!,
      prepay: isPrepay
    };
  });

  const pendingEnrollmentItems = pendingEnrollmentLearners.map(
    ({ learner, hasOverlappingPendingEnrollmentMeetingPurchase }) => ({
      key: learner?.uid,
      checked: !!activity.hasTeacherSchedule
        ? hasOverlappingPendingEnrollmentMeetingPurchase
        : true,
      pendingEnrollment: true,
      hasOverlappingPendingEnrollmentMeetingPurchase:
        activity.hasTeacherSchedule
          ? hasOverlappingPendingEnrollmentMeetingPurchase
          : true,
      disabled:
        !activity.hasTeacherSchedule ||
        hasOverlappingPendingEnrollmentMeetingPurchase,
      learner
    })
  );

  // @ts-ignore
  return [
    ...recentLearnerItems,
    ...otherLearnerItems,
    ...alreadyEnrolledItems,
    ...pendingEnrollmentItems,
    ...newItems
  ];
}

///Mutates either recentLearnerItems or otherLearnerItems or neither to pre-check some of them on the enrollment form.
//Returns whether it checked anything
function chooseCheckedLearners(
  recentLearnerItems: LearnerItemType[],
  otherLearnerItems: LearnerItemType[],
  minAge: number,
  maxAge: number,
  strictAgeRange: boolean,
  onlyAllowSingleLearner: boolean
): boolean {
  const { ageGap: recentLearnerAgeGap, learnerUids: recentLearnersNearRange } =
    getLearnerUidsNearAgeRange({
      learners: _.map(recentLearnerItems, "learner"),
      range: [minAge, maxAge]
    });

  if (recentLearnerAgeGap === 0) {
    setLearnerItemsChecked(
      recentLearnerItems,
      recentLearnersNearRange,
      onlyAllowSingleLearner
    );
    return true;
  }

  const { ageGap: otherLearnerAgeGap, learnerUids: otherLearnersNearRange } =
    getLearnerUidsNearAgeRange({
      learners: _.map(otherLearnerItems, "learner"),
      range: [minAge, maxAge]
    });

  if (otherLearnerAgeGap === 0) {
    setLearnerItemsChecked(
      otherLearnerItems,
      otherLearnersNearRange,
      onlyAllowSingleLearner
    );
    return true;
  }
  //if neither recent or other learners are in the age range, choose recent only if the class is not a strict age range.
  if (!strictAgeRange) {
    if (recentLearnersNearRange.length > 0) {
      setLearnerItemsChecked(
        recentLearnerItems,
        recentLearnersNearRange,
        onlyAllowSingleLearner
      );
    } else {
      setLearnerItemsChecked(
        otherLearnerItems,
        otherLearnersNearRange,
        onlyAllowSingleLearner
      );
    }
    return true;
  }
  return false;
}

function setLearnerItemsChecked(
  items: $TSFixMe,
  keys: $TSFixMe,
  onlyAllowSingleLearner: $TSFixMe
) {
  if (!items.length) {
    return;
  }

  // if user is checking out with class wallet we only want to check 1 learner
  const learnersToCheck = onlyAllowSingleLearner ? [items[0]] : items;
  learnersToCheck.forEach(
    (li: $TSFixMe) => (li.checked = keys.includes(li.key))
  );
}

getLearnerItems.fragments = {
  enrollment: gql`
    fragment getLearnerItems_enrollment on Enrollment {
      uid
      confirmedAt
      withdrawnAt
      ongoingEndTime
      isPaused
      isEnded
      learner {
        uid
        age
        name
      }
      ...EnrollmentWarnings_enrollment
    }
    ${EnrollmentWarnings.fragments.enrollment}
  `,

  enrollmentWithMeetingPurchases: gql`
    fragment getLearnerItems_enrollmentWithMeetingPurchases on Enrollment {
      uid
      confirmedAt
      withdrawnAt
      ongoingEndTime
      isPaused
      isEnded
      hasOverlappingPendingEnrollmentMeetingPurchase
      learner {
        uid
        age
        name
      }
      ...EnrollmentWarnings_enrollment
    }
    ${EnrollmentWarnings.fragments.enrollment}
  `
};
